import { createTrajectory } from "core/src/core/geometry/trajectory/trajectory-util.js";
import { JUMP_COOLDOWN, JUMP_CHANCE, WORM_RADIUS } from "core/src/core/constants.js";
import * as cf from "core/src/core/core-functions.js";
import * as shapeSpatialRelations from "core/src/core/geometry/shape-spatial-relations.js";
import { createLocalCollision } from "./local-collision";
import * as tronTurnEffect from "core/src/core/power-up/effect-definitions/tron-turn.js";
import * as drunkEffect from "core/src/core/power-up/effect-definitions/drunk.js";
import * as wallHackEffect from "core/src/core/power-up/effect-definitions/wall-hack.js";
import * as switchSlowEffect from "core/src/core/power-up/effect-definitions/switch-slow.js";
import * as gsf from "core/src/core/game-state-functions.js";

// Self power-ups we can predict client-side (apply the instant you touch them).
// Self effects the client applies to its OWN worm the instant it grabs the power-up
// (the server confirms ~a round trip later; syncEffects dedupes). wallHack must be here
// or the prediction never knows you have it: your own worm would hit the wall (and
// report the crash) instead of wrapping, and the active-effect indicator wouldn't show,
// all until the server round-trip - exactly the "bots wrap but I just crash" gap.
// The copy of an own segment that goes to the server (drainSegments' output is only
// ever sent). Our track keeps full precision; the wire copy rounds positions to
// 0.001px and angles to 0.00001rad, and leaves out what the server ignores and sets
// itself (playerId, id, the private _markers). The server parses every player's
// stream and relays the segments to every player, so with 20 players the full
// 17-digit floats were most of both. Times stay exact: they order the tracks.
// Rounding is deterministic, so a segment still starts exactly where the previous
// one ended on the wire, as it does locally.
const WIRE_COORDS = new Set(["startX", "startY", "endX", "endY", "arcCenterX", "arcCenterY", "arcRadius", "size"]);
const WIRE_ANGLES = new Set(["startDirection", "endDirection", "turningVelocity", "arcStartAngle", "arcAngleDiff", "arcEndAngle"]);
export function wireSegment(seg: any): any {
  const out: any = {};
  for (const k in seg) {
    if (k.charCodeAt(0) === 95 || k === "playerId" || k === "id") continue;
    const v = seg[k];
    if (typeof v !== "number") out[k] = v;
    else if (WIRE_COORDS.has(k)) out[k] = Math.round(v * 1e3) / 1e3;
    else if (WIRE_ANGLES.has(k)) out[k] = Math.round(v * 1e5) / 1e5;
    else out[k] = v;
  }
  return out;
}

const PREDICTABLE_SELF_EFFECTS = ["speed", "size", "turningSpeed", "superJump", "wallHack"];

// Effects that compute per-frame dynamic state in an update() (rather than a static
// getter). Online the client OWNS its worm's movement, so it must run these like
// core does offline, or they silently do nothing on the local worm: tronTurn never
// sets turningVelocity (so "turn 90 deg" leaves you unable to turn AT ALL), and drunk
// never wobbles. Keyed by effect type.
const EFFECT_UPDATERS: Record<string, (gs: any, dt: number, e: any) => void> = {
  [tronTurnEffect.type]: tronTurnEffect.update,
  [drunkEffect.type]: drunkEffect.update,
};

// Client-authoritative controller for the local player's own worm.
//
// Your key presses are LOCAL (zero network delay), so your client is the source of
// truth for your worm: it advances the worm's path from your input every frame and
// OWNS that path (the server's copy is ignored for your rendering). No
// reconciliation, no pull, your worm is exactly your input. The path (including the
// jumps/holes the client decides) is streamed to the server, which validates it and
// uses it for collision (death stays server-authoritative).
//
// Segments are kept SHORT (STEP) because the host paints the trail at each segment's
// start, so short segments are needed for a continuous collision trail.
const STEP = 0.025; // segment length, seconds of game time (~2.5px at base speed)

export function createOwnWormController(playerId: any, opts: { disableJumps?: boolean; getOpponentClipTime?: () => number } = {}) {
  const disableJumps = !!opts.disableJumps; // test peers can run a clean, hole-free trail
  // Only collide our head against opponent trail the player can actually SEE: paint
  // opponents up to the smooth render clock, not the freshest received segment.
  // Without this you die against ~a render-delay of opponent trail your screen
  // hasn't drawn yet ("die in the open"). Undefined = paint all received (legacy).
  const getOpponentClipTime = opts.getOpponentClipTime;
  let steering = 0;
  // The effect/power-up event cursors live on the SHARED gameState (below), not here:
  // with two local players there are two controllers draining the same event arrays, and
  // a per-controller cursor made each one apply every effect (double speed/size/etc.).
  const pickedUp = new Map<any, number>(); // power-up id -> game-time we predicted grabbing it
  let revertedPickups = false; // undid speculative grabs after a server-confirmed death
  // A Respawn round (core respawn.js): how far into gameEvents we have read for
  // our new worms (worm_spawned) and the colours leaving the board (trail_cleared).
  let eventsRead = 0;

  // A player can control more than one worm at once (the twin power-up spawns a second
  // worm sharing your playerId). Each worm we own is a "slot" with its own track,
  // collision, jump state and stream cursor; ALL slots share the one steering input, so
  // both of your worms follow the same keys. A lone worm is just one slot - unchanged.
  interface WormSlot {
    wormId: any;
    trackId: string;
    collision: ReturnType<typeof createLocalCollision>;
    jump: { remainingJumpTime: number; timeSinceLastJump: number };
    sendCursor: number; // index of the next own segment to stream for this worm
    crashReported: boolean; // told the server this worm crashed (report once)
    pendingTurn: number; // one-shot instant direction kink to apply on the next segment
    // Where this worm's head is MIRRORING (wall-hack wrap) right now: the on-screen wrap(s)
    // of the head, recomputed each frame we predict it while straddling an edge, [] otherwise.
    // Authoritative - we own this worm - so the renderer draws mirror heads here with no
    // clock/staleness guessing. Frozen on death (predictSlot returns early, never resetting it).
    mirrorHeads: { x: number; y: number; direction: number }[];
  }
  const slots: WormSlot[] = [];
  // Twin pickups predicted this frame, spawned at predict()'s top (not mid slot-loop).
  const pendingTwins: { wormId: any; puId: any }[] = [];
  const spawnedTwinPus = new Set<any>(); // power-up ids we've already spawned a twin for
  const newWorms: any[] = []; // twin registrations awaiting send to the host (drainNewWorms)
  let twinCounter = 0;
  const TWIN_TIMELEFT = 10000; // mirrors core twin INITIAL_TIMELEFT (the spawn-immunity grace)
  // Switcharoonie hand-off. The host runs a freeze -> swap -> go sequence (see match-runner). We
  // hold our worm at `freezeTime`, apply the owner swap to our worms (game_update doesn't carry
  // playerId), then at `goTime` re-bind to whatever worm now wears our id and resume. Server-
  // authoritative: we never predict the switch, we just obey these two timestamps.
  let switchState: { freezeTime: number; goTime: number | null; assignments: any; applied?: boolean } | null = null;
  let switchStatus: "switching" | "frozen" | null = null; // for the debug view: are we mid-switcharoonie?

  // `sendCursor` = index of the next segment to stream. 0 for a brand-new worm (send its whole
  // trail). After a switcharoonie hand-off the taken-over worm's track already holds its full
  // history, so we seed the cursor at the current end (see the re-bind in predict) to stream only
  // the fresh segments we lay from here on, never re-shipping the backlog.
  function addSlot(wormId: any, trackId: string, sendCursor = 0): WormSlot {
    const slot: WormSlot = {
      wormId,
      trackId,
      collision: createLocalCollision(() => slot.trackId),
      jump: { remainingJumpTime: 0, timeSinceLastJump: 0 },
      sendCursor,
      crashReported: false,
      pendingTurn: 0,
      mirrorHeads: [],
    };
    slots.push(slot);
    return slot;
  }

  // Maintain gameState.effects from the streamed effectEvents and refresh each
  // active effect's timeLeft for game-time `t`, so core's effect-aware getters
  // (getWormSpeed etc.) compute the right values for our worm. Expired effects are
  // dropped. (We don't run effect.update(); the timeLeft-based effects we support
  // don't need it, and the rest degrade gracefully.)
  function syncEffects(gameState: any, t: number) {
    if (!gameState.effects) gameState.effects = [];
    const events = gameState.effectEvents || [];
    if (gameState._effectSyncCursor === undefined) gameState._effectSyncCursor = 0;
    for (; gameState._effectSyncCursor < events.length; gameState._effectSyncCursor++) {
      const ev = events[gameState._effectSyncCursor];
      if (ev.type === "spawn" && ev.effect) {
        const e = { ...ev.effect };
        // Already present by id (e.g. an effect seeded into the game_start snapshot, which also
        // carries its spawn event)? Don't add a second copy.
        if (e.id != null && gameState.effects.some((x: any) => x.id === e.id)) continue;
        const life = e.duration !== undefined ? e.duration : e.timeLeft;
        e._expiry = life !== undefined ? ev.time + life : Infinity;
        // If we already predicted this pickup locally, replace the predicted effect
        // with the server's authoritative one (avoid stacking the same effect twice).
        const pi = gameState.effects.findIndex((x: any) => x._predicted && x.type === e.type && x.wormId === e.wormId);
        if (pi !== -1) gameState.effects.splice(pi, 1);
        gameState.effects.push(e);
      } else if (ev.type === "despawn") {
        const i = gameState.effects.findIndex((e: any) => e.id === ev.effectId);
        if (i !== -1) gameState.effects.splice(i, 1);
      }
    }
    for (let i = gameState.effects.length - 1; i >= 0; i--) {
      const e = gameState.effects[i];
      if (e._expiry !== undefined && e._expiry !== Infinity) {
        e.timeLeft = e._expiry - t;
        if (e.timeLeft <= 0) gameState.effects.splice(i, 1);
      }
    }
  }

  function setSteering(s: number) {
    steering = s;
  }

  // The round's gameState, handed over by reset(). The game canvas reads gameState
  // from React props, so after a round starts it can run a frame or two on the
  // PREVIOUS round's state before React re-renders. predict() on that state would
  // build our slot (and its fresh collision grid) from the old round's track, and
  // the old trail would stay in the grid all round, invisible: a crash in the open.
  // So once a round's state is known, frames on any other state are skipped.
  let roundState: any = null;

  function reset(gameState?: any) {
    roundState = gameState || null;
    steering = 0;
    slots.length = 0;
    pickedUp.clear();
    pendingTwins.length = 0;
    spawnedTwinPus.clear();
    newWorms.length = 0;
    twinCounter = 0;
    revertedPickups = false;
    eventsRead = 0;
    switchState = null;
    switchStatus = null;
  }

  // The host put us back in (a Respawn round): our dead worms' slots go, and the
  // new worm gets one, starting at the spot the host chose, holing for its gap.
  // The host drives nothing of it: from here it is ours like any worm.
  function respawnSlot(gameState: any, e: any) {
    const worm = gameState.worms.find((w: any) => w.id === e.wormId);
    if (!worm || worm.alive === false) return; // (an old spawn, from a snapshot)
    // a crashed worm's slot is taken over (its collision grid has every opponent
    // painted already: a fresh one would paint the whole round in one frame)
    let reuse: WormSlot | null = null;
    for (let i = slots.length - 1; i >= 0; i--) {
      const w = gameState.worms.find((wo: any) => wo.id === slots[i].wormId);
      if (w && w.alive !== false) continue;
      if (!reuse) reuse = slots[i];
      slots.splice(i, 1);
    }
    const trackId = worm.trackId || `${playerId}_${e.wormId}`;
    let segs = gameState.wormPathSegments[trackId];
    if (!segs || !segs.length) {
      segs = gameState.wormPathSegments[trackId] = [{
        startX: e.x, startY: e.y, endX: e.x, endY: e.y,
        startDirection: e.direction, endDirection: e.direction, startTime: e.time, endTime: e.time,
        duration: 0, speed: 0, turningVelocity: 0, size: e.radius, jump: false,
        playerId, wormId: e.wormId, id: trackId, _own: true,
      }];
    }
    // (the host has the spawn point: stream only what we lay from here)
    let slot: WormSlot;
    if (reuse) {
      slot = reuse;
      slot.wormId = e.wormId;
      slot.trackId = trackId;
      slot.collision.rebind();
      slot.jump = { remainingJumpTime: 0, timeSinceLastJump: 0 };
      slot.sendCursor = segs.length;
      slot.crashReported = false;
      slot.pendingTurn = 0;
      slot.mirrorHeads = [];
      slots.push(slot);
    } else {
      slot = addSlot(e.wormId, trackId, segs.length);
    }
    slot.jump.remainingJumpTime = e.gap || 0;
  }

  // We died per the server at `deathTime`. Only the power-ups we predicted grabbing AFTER
  // that (while over-predicting past the real crash point) were never ours - undo just
  // those, so they reappear and their speculative self-effect is dropped. Power-ups grabbed
  // BEFORE we died are legitimately ours: keep them taken (a genuine grab stays hidden via
  // its server despawn regardless). Reverting ALL of them used to make a power-up grabbed
  // right before dying flash back to full size, then vanish again when its despawn landed.
  function revertSpeculativePickups(gameState: any, wormId: any, deathTime: number) {
    const reverted: any[] = [];
    pickedUp.forEach((time, id) => {
      if (time > deathTime) reverted.push(id);
    });
    const effects = gameState.effects || [];
    reverted.forEach((id) => {
      pickedUp.delete(id);
      for (let i = effects.length - 1; i >= 0; i--) {
        if (effects[i]._predicted && effects[i].wormId === wormId && effects[i]._predictedFrom === id) {
          effects.splice(i, 1);
        }
      }
    });
  }

  // Maintain gameState.powerUps from the streamed powerUpEvents so we can detect
  // pickups locally.
  function syncPowerUps(gameState: any) {
    if (!gameState.powerUps) gameState.powerUps = [];
    const events = gameState.powerUpEvents || [];
    if (gameState._powerUpSyncCursor === undefined) gameState._powerUpSyncCursor = 0;
    for (; gameState._powerUpSyncCursor < events.length; gameState._powerUpSyncCursor++) {
      const ev = events[gameState._powerUpSyncCursor];
      if (ev.type === "spawn" && ev.powerUp) {
        gameState.powerUps.push(ev.powerUp);
      } else if (ev.type === "despawn") {
        const i = gameState.powerUps.findIndex((p: any) => p.id === ev.id);
        if (i !== -1) gameState.powerUps.splice(i, 1);
        // If we speculatively grabbed this power-up, drop the effect we speculatively
        // applied: a real grab has the server's own effect-spawn re-add it authoritatively
        // (synced right after); a contested one (an opponent actually took it) correctly
        // stops showing a buff we never received. Do NOT clear it from `pickedUp` - it's
        // already despawned, so leaving it "picked up" keeps it cleanly hidden. (Un-hiding
        // it here made your OWN pickups flash back to full size then shrink out - a blink.)
        if (pickedUp.has(ev.id)) {
          const effects = gameState.effects || [];
          const ei = effects.findIndex((e: any) => e._predictedFrom === ev.id);
          if (ei !== -1) effects.splice(ei, 1);
        }
      }
    }
  }

  // Predict picking up self power-ups: if our head overlaps one, apply its effect
  // immediately (the server confirms it ~a round trip later; syncEffects dedupes).
  function predictPickups(gameState: any, x: number, y: number, size: number, wormId: any, t: number) {
    const head = { centerX: x, centerY: y, radius: size };
    const powerUps = gameState.powerUps || [];
    for (let i = 0; i < powerUps.length; i++) {
      const p = powerUps[i];
      if (pickedUp.has(p.id)) continue;
      if (!shapeSpatialRelations.intersects(head, p.shape)) continue;
      // You consume ANY power-up your head touches (the server picks up whatever you
      // run over, whoever the effect hits), so hide it instantly for green/red/blue.
      pickedUp.set(p.id, t);
      // Apply the effect locally only for the self effects we can predict; "others"
      // (red) effects hit the bots and arrive from the server.
      const affectsSelf = p.affects === "self" || p.affects === "all";
      if (affectsSelf && PREDICTABLE_SELF_EFFECTS.indexOf(p.effectType) !== -1) {
        const dur = p.effectDuration;
        // A "self" (green) effect lands on the grabbing worm only; an "all" (blue) effect lands on
        // EVERY worm, so it must hit ALL the worms we own (a twin too), not just the one that grabbed
        // it - otherwise our other worm has no wall-hack and crashes. Enemies get theirs from the
        // server. Guard against double-applying to a worm that already carries this power-up's effect.
        const targets = p.affects === "all" ? slots.map((s) => s.wormId) : [wormId];
        for (const tgt of targets) {
          if (gameState.effects.some((e: any) => e.wormId === tgt && e._predictedFrom === p.id)) continue;
          gameState.effects.push({
            type: p.effectType,
            strength: p.effectStrength,
            duration: dur,
            timeLeft: dur,
            wormId: tgt,
            _predicted: true,
            _predictedFrom: p.id, // so syncPowerUps can undo it if the server despawns the power-up to someone else
            _expiry: dur !== undefined ? t + dur : Infinity,
          });
        }
      } else if (affectsSelf && p.effectType === "twin") {
        // Twin spawns a WHOLE worm, not an effect. Client-authoritative: we own the twin's id +
        // path. Defer the spawn to predict()'s top so we don't grow `slots` mid slot-loop.
        if (!pendingTwins.some((pt) => pt.puId === p.id) && !spawnedTwinPus.has(p.id)) {
          pendingTwins.push({ wormId, puId: p.id });
        }
      } else if (p.effectType === "wormSwitch") {
        // Grabbing a switcharoonie stops our own worm THE MOMENT we grab it (client-authoritative),
        // pending the host's authoritative freeze -> swap -> go. Race doesn't matter: if someone else
        // grabbed it first we still just want to stop and hold until the host resyncs everyone, so we
        // don't wait for the host's switch_freeze to begin freezing. The assignments (the actual
        // swap) only arrive from the host later; until then we hold with no swap applied.
        if (!switchState) switchState = { freezeTime: t, goTime: null, assignments: null };
      }
    }
  }

  // Every track our worm(s) currently own. A track key is a worm's STABLE identity (worm.trackId),
  // not its owner, so ownership is resolved live via the worm's playerId - which is what lets a
  // switcharoonie hand a worm over without re-keying its trail. Skip wall-hack mirror ("#") tracks.
  function ownTracks(gameState: any): string[] {
    const tracks = gameState.wormPathSegments;
    const out: string[] = [];
    for (const id of Object.keys(tracks)) {
      if (id.indexOf("#") !== -1) continue;
      const segs = tracks[id];
      if (!segs.length) continue;
      const worm = gameState.worms.find((w: any) => w.id === segs[0].wormId);
      if (worm && worm.playerId === playerId) out.push(id);
    }
    return out;
  }
  function findTrack(gameState: any): string | null {
    const ts = ownTracks(gameState);
    return ts.length ? ts[0] : null;
  }

  // A twin power-up is a green (self) power-up that, instead of an effect, spawns a whole
  // SECOND worm. It is client-authoritative like our primary worm: the instant we grab it we
  // pick the twin's id + spawn point, render it locally, stream its path, and register it with
  // the host (drainNewWorms). The host ADOPTS that worm - it no longer spawns its own - so
  // there is no server id to reconcile. Lose a contested grab and the host rejects the
  // registration; we remove it (removeWorm). Bots / offline twins are still server-spawned.
  function spawnTwin(gameState: any, originalId: any, puId: any) {
    const orig = slots.find((s) => s.wormId === originalId);
    if (!orig) return;
    const origSegs = gameState.wormPathSegments[orig.trackId];
    if (!origSegs || origSegs.length === 0) return;
    // Spawn at the original's CURRENT head (the unpainted front of the trail), exactly like
    // core's twin.activate, so the twin doesn't instantly crash into its own fresh trail.
    const head = origSegs[origSegs.length - 1];
    const spawnX = head.endX, spawnY = head.endY, spawnT = head.endTime, baseDir = head.endDirection, size = head.size;
    const origWorm = gameState.worms.find((w: any) => w.id === originalId);
    const twinId = playerId + "_twin" + (twinCounter++);
    const twinTrackId = playerId + "_" + twinId;
    // Split SYMMETRICALLY like core's twin.activate: the original kinks +PI/8, the twin starts
    // at -PI/8 (a PI/4 total split). The original's kink is a one-shot next-segment turn.
    orig.pendingTurn = Math.PI / 8;
    const dir = baseDir - Math.PI / 8;
    if (!gameState.worms.some((w: any) => w.id === twinId)) {
      // trackId is the twin's OWN stable key (the spread would copy the original's).
      gameState.worms.push({ ...(origWorm || {}), id: twinId, trackId: twinTrackId, playerId, alive: true, direction: dir, centerX: spawnX, centerY: spawnY });
    }
    gameState.wormPathSegments[twinTrackId] = [{
      startX: spawnX, startY: spawnY, endX: spawnX, endY: spawnY,
      startDirection: dir, endDirection: dir, startTime: spawnT, endTime: spawnT,
      duration: 0, speed: 0, turningVelocity: 0, size, jump: false,
      playerId, wormId: twinId, id: twinTrackId, _own: true,
    }];
    spawnedTwinPus.add(puId);
    // Inherit the original's currently-active effects (wall-hack, speed, ...), exactly like core's
    // twin.activate, so the twin keeps its buffs (e.g. phases through walls) instead of crashing.
    // Client-authoritative, like everything about our twin: we clone them onto the twin LOCALLY
    // (keeping each effect's predicted-rollback + expiry bookkeeping, so a lost contested grab on
    // the original rolls back the twin's copy too) AND ship the plain effect data in the
    // registration so the host applies the SAME set - it never re-derives from its own worm.
    if (!gameState.effects) gameState.effects = [];
    const inherited = gameState.effects.filter((e: any) => e.wormId === originalId && e.type !== "twin");
    // Mark the copies _predicted so syncEffects DEDUPES the host's echo of them (addEffect streams a
    // spawn event for each): without this, an inherited effect that came from an already-confirmed
    // (non-predicted) original wasn't recognised as ours, so the server's copy stacked on top - the
    // twin visibly gained a SECOND set of the effects ~a round trip after spawning.
    for (const e of inherited) gameState.effects.push({ ...e, wormId: twinId, _predicted: true });
    const inheritedForHost = inherited.map((e: any) => ({
      type: e.type, strength: e.strength, duration: e.duration, timeLeft: e.timeLeft, wormId: twinId,
    }));
    // The mutual "twin" immunity effects (computed ONCE). Apply them LOCALLY now - marked
    // predicted so syncEffects swaps in the server's copy when it confirms - so the buff shows
    // on our twin immediately (client-authoritative), not a round-trip after the host echoes it.
    // Ship the same effects in the registration so the host applies them too (its collision
    // needs them so it doesn't kill the fresh twin overlapping the original's trail).
    const twinEffects = [
      { type: "twin", wormId: twinId, twinWormId: originalId, timeLeft: TWIN_TIMELEFT },
      { type: "twin", wormId: originalId, twinWormId: twinId, timeLeft: TWIN_TIMELEFT },
    ];
    for (const e of twinEffects) gameState.effects.push({ ...e, _predicted: true, _predictedFrom: puId, _expiry: Infinity });
    // The host clones the original's distanceTravelled bookkeeping (it has that worm), so we
    // don't ship it.
    newWorms.push({
      wormId: twinId,
      originalWormId: originalId,
      puId,
      spawn: { centerX: spawnX, centerY: spawnY, direction: dir, size },
      effects: [...twinEffects, ...inheritedForHost],
    });
    return addSlot(twinId, twinTrackId);
  }

  // The host rejected a twin we spawned (we lost a contested grab): remove the worm, its
  // track, and our slot so it stops rendering. Idempotent.
  function removeWorm(gameState: any, wormId: any) {
    const i = slots.findIndex((s) => s.wormId === wormId);
    if (i !== -1) {
      delete gameState.wormPathSegments[slots[i].trackId];
      slots.splice(i, 1);
    }
    const wi = gameState.worms.findIndex((w: any) => w.id === wormId);
    if (wi !== -1) gameState.worms.splice(wi, 1);
    // Drop the predicted twin effects we added for it (on the twin AND its original partner).
    const effs = gameState.effects || [];
    for (let j = effs.length - 1; j >= 0; j--) {
      if (effs[j]._predicted && effs[j].type === "twin" && (effs[j].wormId === wormId || effs[j].twinWormId === wormId)) effs.splice(j, 1);
    }
  }

  // Mirror core's updateWormJumps so the client owns its holes. Returns whether the
  // worm is mid-jump (segment leaves no trail). `j` is the per-worm jump state.
  // `baseSpeed` (the worm's own speed, without effects) sets the hole's length, as
  // in core: measured by the effective speed, a hole opened in the crawl after a
  // switcharoonie (0.1x) lasted ten holes' time and stretched as the worm sped up.
  function updateJump(j: { remainingJumpTime: number; timeSinceLastJump: number }, dt: number, speed: number, baseSpeed: number = speed): boolean {
    if (disableJumps) return false; // hole-free trail (test peers)
    if (speed <= 0) return false; // no jumps while stationary (start phase)
    if (j.remainingJumpTime <= 0) {
      j.timeSinceLastJump += dt;
      if (j.timeSinceLastJump > JUMP_COOLDOWN) {
        const notJumpedElapsed = j.timeSinceLastJump - JUMP_COOLDOWN;
        const notJumpedMax = 1 / JUMP_CHANCE;
        // The chance climbs to certain as the window runs out. If the timer is
        // already PAST the window (it was set from a long time without a hole: a
        // reattach, a hitch, a tab that slept), the division goes negative and
        // the roll could never succeed again: the worm then never jumped for the
        // rest of the round. Overdue means jump now.
        const left = notJumpedMax - (notJumpedElapsed - dt);
        const chance = left <= dt ? 1 : dt / left;
        if (chance >= Math.random()) {
          j.remainingJumpTime = (2 * WORM_RADIUS + 4 * WORM_RADIUS) / (baseSpeed > 0 ? baseSpeed : speed);
          j.timeSinceLastJump = 0;
        }
      }
    } else {
      j.remainingJumpTime -= dt;
    }
    return j.remainingJumpTime > 0;
  }

  // Advance every worm we own up to targetTime in short STEP segments. Called every
  // frame by the predicted-worm renderer. Returns the PRIMARY (first) worm's track id,
  // or null before our track exists.
  function predict(gameState: any, targetTime: number): string | null {
    if (roundState && gameState !== roundState) return null; // a stale frame from the last round (see roundState)
    // Switcharoonie hand-off (see switchState). Server-authoritative: hold at the freeze time, apply
    // the owner swap, then at go re-bind to our new worm from its frozen position.
    // Switcharoonie GO (server-driven): apply the owner swap + re-bind. Nothing switches during the
    // freeze; there, predictSlot just lays stationary segments (a REAL freeze - see the `frozen`
    // flag below - so un-freezing doesn't dump the whole frozen duration as one long moving segment).
    if (switchState && switchState.goTime != null && targetTime >= switchState.goTime && !switchState.applied) {
      // The delta stream carries new tracks but not the worms' new playerId, so apply it, then
      // re-bind: ownTracks resolves to the worm now assigned to us, seeded from its frozen head.
      // A client can run SEVERAL predictors over the SAME shared gameState (local multiplayer) -
      // the swap must be applied exactly ONCE per switch, not once per predictor (twice = the
      // squared permutation: every worm lands on the wrong owner, this client binds worms the
      // server thinks belong to someone else, and its segments are rejected). Guard per goTime.
      if (switchState.assignments && (gameState as any)._switchAppliedAt !== switchState.goTime) {
        for (const w of gameState.worms) {
          const to = switchState.assignments[w.playerId];
          if (to !== undefined) w.playerId = to;
        }
        (gameState as any)._switchAppliedAt = switchState.goTime;
      }
      slots.length = 0;
      // PREDICT the switch_slow locally, now, for the worm(s) we own after the swap. The server
      // applies switchSlow at GO on its side, but its effect event reaches us at least a tick later -
      // so without this our first post-go segments run at FULL speed (10x the slowed speed) and the
      // worm visibly jumps, then snaps to a crawl when the event lands. Same client-authoritative
      // pattern as every predicted green effect: push a _predicted copy; syncEffects swaps in the
      // server's authoritative one when it arrives (matched on _predicted + type + wormId).
      if (!gameState.effects) gameState.effects = [];
      const goT = switchState.goTime;
      for (const w of gameState.worms) {
        if (w.playerId !== playerId || w.alive === false) continue;
        // Predict a FRESH slow for THIS switch (keyed by goTime), even if an earlier switch's slow is
        // still ticking down - the server STACKS a new slow per switch (applyWormSwitch adds one for
        // every alive worm every time), so skipping "because a slow already exists" left back-to-back
        // switches (<3s apart, e.g. queued double-pickups) with no new slow: the client then ran the
        // first post-go ticks at full speed once the old ramp expired - a visible jump.
        if (gameState.effects.some((e: any) => e.type === "switchSlow" && e.wormId === w.id && e._predictedGo === goT)) continue;
        const slow: any = switchSlowEffect.activate({ wormId: w.id } as any); // same shape/duration as the server's
        slow._predicted = true;
        slow._predictedGo = goT;
        slow._expiry = goT + slow.duration;
        gameState.effects.push(slow);
      }
      // Keep switchState alive (applied=true) rather than clearing it: the worm we just took over is
      // an OPPONENT copy that lagged behind (render-delayed), so its head sits BEHIND goTime. predictSlot
      // must keep that catch-up (head -> goTime) FROZEN and only start moving past goTime - otherwise it
      // fills the gap with moving segments and the worm shoots forward at un-freeze (the "jump"). We drop
      // switchState after predictSlot below, once every slot has caught up past goTime.
      switchState.applied = true;
    }
    // Debug view: "switching" once grabbed/told, "frozen" once held; nothing once we've gone (applied).
    switchStatus = !switchState || switchState.applied ? null : (targetTime >= switchState.freezeTime ? "frozen" : "switching");
    // Bootstrap a slot for each worm we currently own (also how we re-bind after a switcharoonie:
    // predict() cleared the slots at "go", and now we pick up whatever worm(s) wear our id, each
    // from its own stable track, seeded at its frozen head). ALWAYS seed the send cursor at the
    // track's current end: whatever history the track already holds (the round's spawn segment, a
    // switcharoonie hand-off, or a whole reconnect snapshot) is the server's own data and must not
    // be re-streamed. A reconnect snapshot in particular still carries our pre-disconnect segments
    // with their _own marker (the server adopts them verbatim), and a 0 cursor made drainSegments
    // bulk re-send that backlog: the server re-adopted second-old segments as new movement,
    // teleported the worm back along them, double-counted distanceTravelled past the immunity
    // window, and killed it on trail that was immune when originally laid. Only a brand-new
    // client-spawned track (a twin, see spawnTwin) starts its cursor at 0.
    const respawnRound = !!gameState.respawn;
    if (slots.length === 0) {
      const midRound = gameState.gameTime > (gameState.startPhaseTime || 0);
      for (const t of ownTracks(gameState)) {
        // (a Respawn round leaves us the tracks of worms long dead: only a live one is ours to steer)
        if (respawnRound && (gameState.worms.find((w: any) => w.id === gameState.wormPathSegments[t][0].wormId) || {}).alive === false) continue;
        const segs = gameState.wormPathSegments[t];
        const slot = addSlot(segs[0].wormId, t, segs.length);
        // Jumps are client-owned (slot.jump), and connection state must not change
        // them. Two reconnect seams to neutralize:
        //  - The snapshot's core worm.jump is the SERVER's (the sim ticks it while
        //    the player is suspended, so a reattach can land mid-sim-jump). Nothing
        //    on the client ever decrements it, so isWormJumping's base value would
        //    stay true FOREVER - an endless hole, i.e. an invincible worm. Zero it;
        //    only slot.jump and real (synced) effects decide jumping.
        //  - A fresh slot's cooldown started at 0, so reconnect-spamming reset the
        //    hole cycle every time (a worm with no holes at all). Binding mid-round
        //    (a resume, a switcharoonie) instead RECONSTRUCTS the real phase from
        //    the track itself: the snapshot carries every hole ever laid (the sim's
        //    gap holes included), so "time since the last hole ended" - or, when
        //    reattaching mid-hole, "how much of this hole remains" - is measurable.
        //    The cycle continues exactly where the trail left off: the minimum
        //    distance between holes holds across a reconnect like it never happened.
        const w = gameState.worms.find((wo: any) => wo.id === slot.wormId);
        if (w && w.jump) w.jump = { remainingJumpTime: 0, timeSinceLastJump: 0 };
        if (midRound) {
          const now = gameState.gameTime;
          let ji = -1;
          for (let i = segs.length - 1; i >= 0; i--) { if (segs[i].jump) { ji = i; break; } }
          if (ji === -1) {
            // No hole yet this round: the cooldown has been running since the worm
            // started moving.
            slot.jump.timeSinceLastJump = Math.max(0, now - (gameState.startPhaseTime || 0));
          } else {
            const lastJumpEnd = segs[ji].startTime + (segs[ji].duration || 0);
            if (lastJumpEnd >= now - 0.05) {
              // Reattached mid-hole: finish the standard jump length measured from
              // where this hole's contiguous run began.
              let j0 = ji;
              while (j0 > 0 && segs[j0 - 1].jump) j0--;
              const speed = segs[ji].speed || 100;
              const jumpLen = (2 * WORM_RADIUS + 4 * WORM_RADIUS) / speed;
              slot.jump.remainingJumpTime = Math.max(0, jumpLen - (now - segs[j0].startTime));
              slot.jump.timeSinceLastJump = 0;
            } else {
              slot.jump.timeSinceLastJump = Math.max(0, now - lastJumpEnd);
            }
          }
        }
      }
    }
    // A Respawn round: our new worms, and the colours leaving the board come off
    // our collision grids (the segments are marked already, remote-game.ts).
    const events = gameState.gameEvents;
    for (; eventsRead < events.length; eventsRead++) {
      const e = events[eventsRead];
      if (!e) continue;
      if (e.type === "trail_cleared") {
        for (const s of slots) s.collision.unpaintClear(gameState, e.playerId, e.time);
      } else if (e.type === "worm_spawned" && e.playerId === playerId && !slots.some((s) => s.wormId === e.wormId)) {
        respawnSlot(gameState, e);
      } else if (respawnRound && e.type === "worm_died" && e.playerId === playerId) {
        // only the grabs predicted past that crash were never ours
        revertSpeculativePickups(gameState, e.wormId, e.time);
      }
    }
    if (slots.length === 0) return null;
    // Spawn any twins we predicted grabbing this frame. Client-authoritative: we own the twin
    // and register it with the host (drainNewWorms); the host adopts it, so there's nothing to
    // reconcile. A lost contested grab is undone by the host rejecting it (-> removeWorm).
    for (const pt of pendingTwins.splice(0)) {
      if (slots.some((s) => s.wormId === pt.wormId) && !spawnedTwinPus.has(pt.puId)) {
        spawnTwin(gameState, pt.wormId, pt.puId);
      }
    }
    // Death is server-authoritative and (today) per PLAYER: one player_died stops every
    // worm we own. On that confirmation, undo only the grabs we predicted PAST the crash
    // point (they never happened).
    const deathEvent = gameState.gameEvents.find(
      (e: any) => e.type === "player_died" && e.playerId === playerId
    );
    if (deathEvent && !revertedPickups && !respawnRound) {
      const w = gameState.worms.find((wo: any) => wo.playerId === playerId);
      if (w) {
        revertedPickups = true;
        revertSpeculativePickups(gameState, w.id, deathEvent.time);
      }
    }
    // (a Respawn round: a worm is dead when its own crash is confirmed, not at the player's first one)
    for (const slot of slots) {
      const died = respawnRound ? (gameState.worms.find((w: any) => w.id === slot.wormId) || {}).alive === false : !!deathEvent;
      predictSlot(gameState, slot, targetTime, died);
    }
    // Drop the (applied) switch state once every slot's head has predicted past goTime: the frozen
    // catch-up is done and the worm now moves normally. Kept only for the frame(s) it takes to fill the
    // render-delayed gap up to goTime as stationary segments (see the GO block + `frozen` above).
    if (switchState && switchState.applied && switchState.goTime != null) {
      const goT = switchState.goTime;
      const caughtUp = slots.every((s) => {
        const sg = gameState.wormPathSegments[s.trackId];
        return sg && sg.length && sg[sg.length - 1].endTime >= goT - 1e-9;
      });
      if (caughtUp) switchState = null;
    }
    return slots.length ? slots[0].trackId : null;
  }

  // Advance one worm slot's track to targetTime, using the shared steering.
  function predictSlot(gameState: any, slot: WormSlot, targetTime: number, serverDied: boolean) {
    const segs = gameState.wormPathSegments[slot.trackId];
    if (!segs || segs.length === 0) return;
    const worm = gameState.worms.find((w: any) => w.id === slot.wormId);
    // Stop advancing once dead from any source: server confirmation, or our own
    // client-side crash detection (so the worm stops the instant it crashes).
    if (!worm || serverDied || slot.collision.isDead()) return;

    // Wall-hack mirror head: a straddling step below refreshes slot.mirrorHeads and sets `straddled`.
    // We deliberately do NOT clear mirrorHeads at the top of the frame - clearing is a DISTANCE
    // decision made after the step loop: a head sitting right on the wall makes the binary touch
    // test flip on/off frame to frame, so we only drop the mirror once the head is CLEARLY back
    // inside (a small dead-band past the edge). A dead worm returned above, so its value freezes.
    let straddled = false;

    const startPhase = gameState.startPhaseTime || 0;
    const wormId = slot.wormId;
    const player = gameState.players.find((p: any) => p.id === playerId);

    slot.collision.paint(gameState, getOpponentClipTime ? getOpponentClipTime() : undefined);
    syncPowerUps(gameState);

    let guard = 0;
    // Advance so the LAST segment ends exactly at targetTime (now), in chunks no
    // longer than STEP. Landing exactly (rather than a STEP short, or overshooting
    // past it) means the head segment is freshly built THIS frame with the CURRENT
    // steering every frame, just like local play steps the worm by the frame delta.
    // That removes both the residual position lag and the "stale steering" feel
    // where the head rode a chunk committed up to a STEP ago.
    while (segs[segs.length - 1].endTime < targetTime - 1e-9 && guard++ < 400) {
      const last = segs[segs.length - 1];
      const t0 = last.endTime;
      const inStart = t0 < startPhase - 1e-9;
      let dur = Math.min(STEP, targetTime - t0); // final chunk ends exactly at now
      if (inStart && t0 + dur > startPhase) dur = startPhase - t0; // split at the boundary
      // Apply power-ups via the engine's own math: set our steering and refresh
      // active effects, then read effect-aware speed / turn / size / jump.
      if (player) player.steering = steering;
      syncEffects(gameState, t0);
      // Run dynamic effects' update() for THIS worm (like core's updateEffects), so
      // tronTurn computes its turn and drunk its wobble before we read speed/turning.
      for (const e of gameState.effects) {
        if (e.wormId === wormId && EFFECT_UPDATERS[e.type]) EFFECT_UPDATERS[e.type](gameState, dur, e);
      }
      const size = cf.getWormRadius(gameState, wormId);
      // Switcharoonie freeze: past the freeze time (and before go, when switchState clears) the worm
      // is genuinely stopped - a stationary segment that just advances TIME. So un-freezing resumes
      // from the frozen head instead of dumping the whole frozen duration as one long moving segment.
      const frozen = switchState != null && t0 >= switchState.freezeTime - 1e-9 &&
        (switchState.goTime == null || t0 < switchState.goTime - 1e-9);
      const speed = (inStart || frozen) ? 0 : cf.getWormSpeed(gameState, wormId);
      const turning = frozen ? 0 : cf.getWormTurningVelocity(gameState, wormId);
      const baseSpeed = (gameState.worms.find((w: any) => w.id === wormId) || {}).speed;
      const isJump = updateJump(slot.jump, dur, speed, baseSpeed) || cf.isWormJumping(gameState, wormId);
      // A one-shot instant direction kink (the twin power-up splits the original off by
      // +PI/8 at the spawn instant, mirroring core), applied once then cleared.
      // Wall hack may have wrapped the previous segment to the opposite edge; continue
      // the head from that wrapped point, not the segment's (out-of-bounds) end.
      const cont = last._wrapNext || { x: last.endX, y: last.endY, dir: last.endDirection };
      const startDirection = cont.dir + slot.pendingTurn;
      slot.pendingTurn = 0;
      const seg: any = createTrajectory({ startX: cont.x, startY: cont.y, startDirection, speed, turningVelocity: turning, duration: dur });
      // A speed-0 segment is typed "still_arc", which the renderers treat as the start-phase launch
      // (aim arrow + spawn pulse, and the predicted renderer hides the trail). A switcharoonie freeze
      // is NOT aiming, so relabel it "straight" (a zero-length segment - endX/Y are already the start,
      // nothing to draw); only the real start phase keeps still_arc.
      if (frozen && !inStart) seg.type = "straight";
      seg.startTime = t0;
      seg.endTime = t0 + dur;
      seg.size = size;
      seg.jump = isJump;
      seg.playerId = playerId;
      seg.wormId = wormId;
      seg.id = slot.trackId;
      seg._own = true;
      segs.push(seg);
      // Wall hack: if this segment left the map, wrap the worm to the opposite edge,
      // mirroring core addWormPathSegment -> wallHack.updateWorm (emits the ghost-trail
      // segments and teleports the worm). Record where the next head segment continues
      // from so the chain follows the wrap. Server re-derives its own ghosts when it
      // adopts our segments, so we don't stream these.
      if (gsf.hasWormEffect(gameState, wormId, "wallHack") &&
          !shapeSpatialRelations.contains(gameState.map.shape, { centerX: seg.endX, centerY: seg.endY, radius: seg.size })) {
        worm.centerX = seg.endX;
        worm.centerY = seg.endY;
        worm.direction = seg.endDirection;
        // updateWorm paints the ghost-trail cells into whatever grid is bound to
        // playArea - which here is our collision instance's private grid. That paint
        // uses core's own immunity bookkeeping (worm.distanceTravelledFromCells), NOT
        // the local collision's (ownDistFromCells), so leaving it would make the worm
        // instantly "crash" into the trail it just wrapped onto. Shield the collision
        // grid with a throwaway during the call; we only want the teleport + the ghost
        // SEGMENTS (for rendering). The server re-derives authoritative collision.
        // The throwaway is a plain object, not a map-sized array: the wrap paints only
        // a head-sized circle of cells, and `new Array(rows * cols)` (3.6M slots on a
        // 1900 map) on every straddling 25ms step was a big allocation per frame.
        const pa = gameState.playArea;
        const realGrid = pa.grid;
        if (realGrid) pa.grid = {};
        wallHackEffect.updateWorm(gameState, dur, wormId, seg);
        pa.grid = realGrid;
        // Authoritatively record where the head is mirroring THIS frame: the on-screen wrap(s)
        // of the head (the same wrap core paints the ghost trail at). The renderer draws a mirror
        // head at exactly these, so it appears + vanishes in lockstep with the actual crossing -
        // no clock, no staleness, no lingering. Overwrites each straddling step (last one wins).
        // Wrap by the PLAYABLE size (map.shape.width/height, what core wraps by), NOT the outer
        // map.width/height which includes the border - or the mirror lands a border-width past
        // the real one, off-screen, and no head is recorded. Visibility is still tested against
        // the outer bounds (the whole map incl. border is drawn).
        const shp = gameState.map.shape, sw = shp.width, sh = shp.height, vw = gameState.map.width, vh = gameState.map.height, mr = seg.size;
        const heads: { x: number; y: number; direction: number }[] = [];
        if (shp.type === "stadium") {
          // one mirror, through the middle (core's own stadium wrap)
          const m = wallHackEffect.stadiumMirror(shp, seg.endX, seg.endY, seg.endDirection);
          heads.push({ x: m.x, y: m.y, direction: m.direction });
        } else {
          const wraps: [number, number][] = [[-sw, 0], [sw, 0], [0, -sh], [0, sh], [-sw, -sh], [sw, -sh], [-sw, sh], [sw, sh]];
          for (const o of wraps) {
            const mx = seg.endX + o[0], my = seg.endY + o[1];
            if (mx >= -mr && mx <= vw + mr && my >= -mr && my <= vh + mr) heads.push({ x: mx, y: my, direction: seg.endDirection });
          }
        }
        slot.mirrorHeads = heads;
        straddled = true; // head is touching/over a wall this step
        seg._wrapNext = { x: worm.centerX, y: worm.centerY, dir: worm.direction };
      }
      // Picked up a self power-up? Apply its effect now (no server round trip).
      predictPickups(gameState, seg.endX, seg.endY, seg.size, wormId, seg.endTime);
      // Crashed into a wall or our own trail? Stop here, this frame.
      if (slot.collision.checkOwn(gameState)) break;
    }

    // Distance dead-band. If no step straddled this frame, the head isn't over a wall right now -
    // but don't drop the mirror the instant the touch test goes false, or a head parked on the
    // wall (where the test flickers frame to frame) blinks. Only clear once the head is CLEARLY
    // back inside: its whole circle plus a margin fits within the playable rect. Between "just
    // touching" and "clearly inside" we keep the last mirror head, so it stays steady on the wall
    // and vanishes the moment the worm actually pulls away.
    if (!straddled && slot.mirrorHeads.length > 0) {
      // Use the LIVE head, i.e. the last segment's end - NOT worm.centerX, which is only written
      // inside the straddle block above and so is stale (frozen at the last crossing) in open space.
      const head = segs[segs.length - 1];
      const clearlyInside = shapeSpatialRelations.contains(gameState.map.shape, {
        centerX: head.endX,
        centerY: head.endY,
        radius: head.size * 2, // whole head plus a radius of margin fits inside -> clearly away
      });
      if (clearlyInside) slot.mirrorHeads = [];
    }
  }

  // Own segments created since the last drain, across ALL worms we own, to stream to
  // the server. Each segment carries its own wormId, so the host routes it to the
  // right worm (a player can now own more than one).
  function drainSegments(gameState: any): any[] {
    const out: any[] = [];
    for (const slot of slots) {
      const segs = gameState.wormPathSegments[slot.trackId];
      if (!segs) continue;
      for (let i = Math.max(slot.sendCursor, 0); i < segs.length; i++) {
        if (segs[i] && segs[i]._own) out.push(wireSegment(segs[i]));
      }
      slot.sendCursor = segs.length;
    }
    return out;
  }

  return {
    setSteering,
    reset,
    predict,
    drainSegments,
    // Switcharoonie (server-authoritative). switchFreeze: hold our worm at `atTime` and swap owners;
    // switchGo: at `atTime` re-bind to our new worm and resume. See switchState.
    // Debug view: null | "switching" (grabbed/told, not yet held) | "frozen" (held at freeze).
    switchStatus: () => switchStatus,
    switchFreeze: (atTime: number, assignments: any) => {
      // Keep the earlier freeze time if we already grab-froze locally (so the grabber's worm doesn't
      // briefly resume up to the host's later freeze time), and now bind in the host's assignments.
      const freezeTime = switchState ? Math.min(switchState.freezeTime, atTime) : atTime;
      switchState = { freezeTime, goTime: switchState ? switchState.goTime : null, assignments: assignments || {} };
    },
    switchGo: (atTime: number) => { if (switchState) switchState.goTime = atTime; },
    // Twins we spawned this frame that the host hasn't been told about yet. RemoteGame emits
    // each as a "worm_spawn" so the host adopts the worm (id + spawn + immunity) we chose.
    drainNewWorms: (): any[] => newWorms.splice(0),
    // The host rejected a twin (contested grab we lost): drop the worm + its slot.
    removeWorm,
    isPickedUp: (id: any) => pickedUp.has(id),
    // Game-time we predicted grabbing this power-up (its segment endTime), so the renderer
    // can play the OWN pickup's despawn on the same real-time clock as the head - not the
    // render-delayed clock, which would linger the power-up behind your own worm.
    pickupTime: (id: any) => pickedUp.get(id),
    pickupCount: () => pickedUp.size,
    // Every worm we currently control (original + any twin), for the renderer to draw
    // each and GameCanvas to notice a twin appearing mid-round.
    tracks: () => slots.map((s) => ({ wormId: s.wormId, trackId: s.trackId })),
    // Where a worm's head is wall-hack mirroring right now (authoritative), for the renderer
    // to draw its mirror head(s). Empty when it isn't straddling; frozen if it died mid-wrap.
    mirrorHeads: (wormId: any): { x: number; y: number; direction: number }[] => {
      const s = slots.find((sl) => sl.wormId === wormId);
      return s ? s.mirrorHeads : [];
    },
    // wormIds whose local collision just detected a crash, once each - so RemoteGame
    // reports EACH worm's death to the server by wormId (a player can own two).
    drainCrashes: (): any[] => {
      const out: any[] = [];
      for (const s of slots) {
        if (s.collision.isDead() && !s.crashReported) {
          s.crashReported = true;
          out.push(s.wormId);
        }
      }
      return out;
    },
    // What this worm's local collision decided killed it (null while it lives, or when
    // the server called the death and we never saw one): for the death report.
    crashInfo: (wormId: any): any => {
      const s = wormId != null ? slots.find((sl) => sl.wormId === wormId) : slots[0];
      return s ? s.collision.getCrash() : null;
    },
    // True once EVERY worm we own has crashed (so RemoteGame reports our death / stops
    // the lap). A single-worm player is dead the instant that worm crashes, as before.
    isDead: () => slots.length > 0 && slots.every((s) => s.collision.isDead()),
  };
}
